import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {import("../../../common/types/types").PaginationMeta} PaginationMeta
 */

/**
 * @typedef {Object} PayableInvoiceItem
 * @property {number} id
 * @property {string} invoice_no
 * @property {"paid" | "partial" | "unpaid"} status
 * @property {string} total_amount
 * @property {string} paid_amount
 * @property {number} due_amount
 * @property {string} issued_at
 * @property {number} cycle_id
 * @property {boolean} can_pay
 * @property {string} currency
 * @property {boolean} is_addon - true if invoice is for addon 
 */

/**
 * @typedef {Object} GetPayableInvoiceParams
 * @property {number} [page]
 * @property {number} [per_page]
 * @property {object} [filters]   // { status: "partial" | "unpaid" }
 * @property {string} [sort]      // "-issued_at"
 */

export const useGetPayableInvoices = () => {
  const [invoices, setInvoices] = useState(
    /** @type {PayableInvoiceItem[] | null} */(null),
  );
  const [meta, setMeta] = useState(/** @type {PaginationMeta | null} */(null));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();

  /**
   * Fetch payable invoices
   * @async
   * @param {GetPayableInvoiceParams} params
   */
  const getPayableInvoices = async ({
    page = 1,
    per_page = 10,
    filters = {},
    sort = "",
  } = {}) => {
    try {
      setLoading(true);
      setError("");

      const query = new URLSearchParams();

      query.append("page", String(page));
      query.append("per_page", String(per_page));

      Object.keys(filters).forEach((key) => {
        if (filters[key] !== "" && filters[key] !== undefined) {
          query.append(`filter[${key}]`, filters[key]);
        }
      });

      if (sort) query.append("sort", sort);

      const url = `${TENANT_DOMAIN}/billing/invoices/payable?${query.toString()}`;

      const response = await axiosInstance.get(url);

      setInvoices(response?.data?.data ?? []);
      setMeta(response?.data?.meta ?? null);
    } catch (err) {
      setInvoices(null);
      setMeta(null);

      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    invoices,
    meta,
    loading,
    error,
    getPayableInvoices,
  };
};
